/**
 * Fleet billing cycle period math (calendar YYYY-MM-DD, inclusive ranges).
 * Mirrors `apps/svc-fleet/src/utils/fleetBillingCycle.js`.
 */

export type FleetBillingCycleType = 'weekly' | 'biweekly' | 'monthly' | 'custom';

export interface BillingPeriod {
  periodStart: string;
  periodEnd: string;
}

function parseIsoDate(value: string | Date): { y: number; m: number; d: number; iso: string } {
  const iso = value instanceof Date ? value.toISOString().slice(0, 10) : String(value).slice(0, 10);
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) {
    throw new Error(`Invalid date: ${value}`);
  }
  return { y, m, d, iso };
}

function toUtcDate({ y, m, d }: { y: number; m: number; d: number }): Date {
  return new Date(Date.UTC(y, m - 1, d));
}

function formatUtcDate(dt: Date): string {
  return dt.toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  const { y, m, d } = parseIsoDate(iso);
  const dt = toUtcDate({ y, m, d });
  dt.setUTCDate(dt.getUTCDate() + days);
  return formatUtcDate(dt);
}

function daysBetween(startIso: string, endIso: string): number {
  const a = toUtcDate(parseIsoDate(startIso));
  const b = toUtcDate(parseIsoDate(endIso));
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

function daysInMonth(year: number, month1to12: number): number {
  return new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
}

export function addMonthsClamped(iso: string, months: number): string {
  const { y, m, d } = parseIsoDate(iso);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const nd = Math.min(d, daysInMonth(ny, nm));
  return formatUtcDate(toUtcDate({ y: ny, m: nm, d: nd }));
}

export function normalizeCycleType(
  billingCycleType: string | null | undefined
): Exclude<FleetBillingCycleType, 'custom'> {
  const t = String(billingCycleType || 'monthly').toLowerCase();
  if (t === 'weekly' || t === 'biweekly' || t === 'monthly') {
    return t;
  }
  return 'monthly';
}

function fixedDurationDays(cycleType: string): number | null {
  if (cycleType === 'weekly') return 7;
  if (cycleType === 'biweekly') return 14;
  return null;
}

export function lastCompletedPeriod(args: {
  billingCycleType: string;
  billingCycleAnchor: string;
  asOfDate: string;
}): BillingPeriod | null {
  const type = normalizeCycleType(args.billingCycleType);
  const anchor = parseIsoDate(args.billingCycleAnchor).iso;
  const asOf = parseIsoDate(args.asOfDate).iso;

  if (asOf <= anchor) {
    return null;
  }

  const duration = fixedDurationDays(type);
  if (duration) {
    const offset = daysBetween(anchor, asOf);
    const lastIndex = Math.floor(offset / duration) - 1;
    if (lastIndex < 0) {
      return null;
    }
    const periodStart = addDays(anchor, lastIndex * duration);
    const periodEnd = addDays(periodStart, duration - 1);
    return { periodStart, periodEnd };
  }

  let last: BillingPeriod | null = null;
  for (let monthIndex = 0; monthIndex < 1200; monthIndex += 1) {
    const cycleStart = addMonthsClamped(anchor, monthIndex);
    const cycleNext = addMonthsClamped(anchor, monthIndex + 1);
    const cycleEnd = addDays(cycleNext, -1);
    if (cycleEnd < asOf) {
      last = { periodStart: cycleStart, periodEnd: cycleEnd };
      continue;
    }
    break;
  }
  return last;
}

export function nextCycleStart(args: {
  billingCycleType: string;
  billingCycleAnchor: string;
  asOfDate: string;
}): string {
  const type = normalizeCycleType(args.billingCycleType);
  const anchor = parseIsoDate(args.billingCycleAnchor).iso;
  const asOf = parseIsoDate(args.asOfDate).iso;

  if (asOf <= anchor) {
    return anchor;
  }

  const duration = fixedDurationDays(type);
  if (duration) {
    const offset = daysBetween(anchor, asOf);
    const index = Math.floor(offset / duration);
    const currentStart = addDays(anchor, index * duration);
    if (asOf === currentStart) {
      return currentStart;
    }
    return addDays(anchor, (index + 1) * duration);
  }

  for (let monthIndex = 0; monthIndex < 1200; monthIndex += 1) {
    const cycleStart = addMonthsClamped(anchor, monthIndex);
    const cycleNext = addMonthsClamped(anchor, monthIndex + 1);
    if (asOf === cycleStart) {
      return cycleStart;
    }
    if (asOf > cycleStart && asOf < cycleNext) {
      return cycleNext;
    }
    if (asOf < cycleStart) {
      return cycleStart;
    }
  }

  return addMonthsClamped(anchor, 1);
}

/**
 * First cycle start strictly after `asOfDate` — when a deferred price change takes effect
 * (mirrors svc-fleet strictNextCycleStart).
 */
export function strictNextCycleStart(args: {
  billingCycleType: string;
  billingCycleAnchor: string;
  asOfDate: string;
}): string {
  return nextCycleStart({ ...args, asOfDate: addDays(parseIsoDate(args.asOfDate).iso, 1) });
}

/** In-progress billing period containing `asOfDate`. */
export function currentOpenPeriod(args: {
  billingCycleType: string;
  billingCycleAnchor: string;
  asOfDate: string;
}): BillingPeriod | null {
  const type = normalizeCycleType(args.billingCycleType);
  const anchor = parseIsoDate(args.billingCycleAnchor).iso;
  const asOf = parseIsoDate(args.asOfDate).iso;

  if (asOf < anchor) {
    return null;
  }

  const last = lastCompletedPeriod({
    billingCycleType: type,
    billingCycleAnchor: anchor,
    asOfDate: asOf,
  });
  const periodStart = last ? addDays(last.periodEnd, 1) : anchor;
  if (periodStart > asOf) {
    return null;
  }

  const followingStart = nextCycleStart({
    billingCycleType: type,
    billingCycleAnchor: anchor,
    asOfDate: addDays(periodStart, 1),
  });
  return { periodStart, periodEnd: addDays(followingStart, -1) };
}

/** Display "Sep 1, 2026 – Sep 30, 2026". */
export function formatBillingCycleRange(start: string, end: string): string {
  const fmt = (iso: string) => {
    const { y, m, d } = parseIsoDate(iso);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(toUtcDate({ y, m, d }));
  };
  return `${fmt(start)} – ${fmt(end)}`;
}
